import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useYupValidation from "../../../../hooks/useYupValidation";
import { Branch } from "../../../../models/branch";
import { Error } from "../../../../models/error";
import BranchService from "../../../../services/branch.service";
import TextField from "../../../Form/Input";
import Modal from "../../../Generic/Modal";
import { NotificationToast } from "../../../Generic/Toast/NotificationToast";

export interface Props {
  open: boolean;
  branch: Branch;
  closeModal: () => void;
  refetch: () => void;
}

const EditBranchModal = ({ open, branch, closeModal, refetch }: Props) => {
  const { t: translate } = useTranslation();

  const [branchName, setBranchName] = useState<string>(branch.name || "");
  const [branchCode, setBranchCode] = useState<string>(branch.code || "");
  const [branchDescription, setBranchDescription] = useState<string>(branch.description || "");

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const { mutate: updateBranch, isLoading: isUpdating } = useMutation(
    ["onboarding.branch.update"],
    ({ id, branch }: { id: number; branch: Branch }) => BranchService.update(id, branch),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "notification.branch.updated",
        });
        closeModal();
        refetch();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
      },
    }
  );

  const handleSubmit = () => {
    setIsSubmitted(true);

    if (isBranchFormValid() && errors.length === 0) {
      const updatedBranch = {
        ...branch,
        name: branchName,
        description: branchDescription,
        code: branchCode,
      } as Branch;

      if (branch.id) {
        updateBranch({ id: branch.id, branch: { ...updatedBranch } });
      }
    }
  };

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
  });

  const handleClearInputs = () => {
    setBranchName("");
    setBranchDescription("");
    setBranchCode("");
  };

  const isBranchFormValid = () => {
    try {
      schema.validateSync(
        {
          code: branchCode,
          name: branchName,
          description: branchDescription,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const handleVerifyMandatoryFields = () => {
    if (branchName.trim() && branchCode.trim() && branchDescription.trim()) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (isSubmitted) isBranchFormValid();
  }, [branchName, branchDescription, branchCode]);

  return (
    <Modal
      open={open}
      title={translate("editBranch")}
      handleClose={closeModal}
      data-cy="onboardingBranchEditModal"
      dataCyTitle="onboardingBranchEditModalTitle"
      showActionButtons={true}
      actionButtonProps={{
        textCancel: translate("cancel"),
        dataCyCancel: translate("cancelButton"),
        onCancel: () => {
          closeModal();
          handleClearInputs();
        },
        dataCySave: "submitChangesButton",
        textSave: translate("save"),
        saveDisabled: errors.length > 0 || !handleVerifyMandatoryFields(),
        onSave: handleSubmit,
        loading: isUpdating,
      }}
    >
      <Grid container direction="row" rowSpacing={2} columnSpacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="branchName"
            data-cy="branchName"
            placeholder={translate("name")}
            label={translate("branchName")}
            value={branchName}
            required
            error={fieldHasError("name")}
            errorMessage={getErrorByField("name")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBranchName(event?.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="branchCode"
            data-cy="branchCode"
            placeholder={translate("code")}
            label={translate("code")}
            value={branchCode}
            required
            error={fieldHasError("code")}
            errorMessage={getErrorByField("code")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBranchCode(event?.target.value);
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="branchDescription"
            data-cy="branchDescription"
            placeholder={translate("description")}
            label={translate("description")}
            value={branchDescription}
            required
            error={fieldHasError("description")}
            errorMessage={getErrorByField("description")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setBranchDescription(event?.target.value);
            }}
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default EditBranchModal;
